/* ===================================================================
   PRIVATE FEEDBACK  (feedback gate)  -- faithful 1:1 port of the Claude
   Design prototype "Private feedback" screen
   (Rhody Reviews.dc.html desktop 1165-1227, mobile 2428-2462).

   The prototype screen is the private-feedback INBOX: a policy banner, two
   stat tiles (open / total this month), and a list of private notes each
   with a Reply and a Mark-resolved / Reopen toggle. Presentation is ported
   exactly; styling lives in styles/feedback.css.

   HARD PRODUCT RULE #4 -- NO SCORE-BASED REVIEW-GATING. PERMANENT.
   Rhody Reviews asks EVERY customer for the public Google review link the
   same way. It does NOT, and CANNOT, screen, sort, or route customers by
   predicted rating. The private channel is offered to EVERYONE in parallel,
   never as a "below threshold" detour -- the customer chooses. FTC 16 CFR
   Part 465 + Google review policy. There is deliberately NO NPS, NO star
   threshold, and NO gating/scoring control on this screen, by design. The
   policy banner states this plainly, per the prototype copy.

   DATA / WIRING NOTE:
   - Config (enabled / route_to / private_intro) is real, wired to
     GET/PUT /api/v1/feedback-gate via useFeedbackGate/useUpdateFeedbackGate.
     `enabled` gates the channel-off banner (channel off -> off state).
   - The inbox list has NO backend endpoint yet (there is no /feedback-inbox
     API; verified GET /feedback-gate returns config only). Until that
     endpoint exists the inbox renders an HONEST EMPTY STATE and the stat
     tiles read zero. The prototype's three seeded notes (Marcus Bell /
     Priya Nair / Dan Ortiz) were shipping to production as if they were
     real customers -- audit C2. NEVER re-seed sample notes here; wire the
     list to a real /feedback-inbox feed when one exists (the per-note
     renderer below is kept for that).
   =================================================================== */
import { useMemo, useState } from 'react';
import { useFeedbackGate } from '../hooks';
import '../styles/feedback.css';

/* Shield icon used by the policy banner (ported from the prototype SVG). */
function ShieldIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      className="pf-policy__icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}


type FeedbackStatus = 'open' | 'resolved';
type PrivateNote = {
  id: string;
  name: string;
  initial: string;
  color: string;
  topic: string;
  ago: string;
  text: string;
  status: FeedbackStatus;
};

/* NO SEED NOTES. The inbox starts honestly empty until a real
   /feedback-inbox endpoint feeds it (audit C2: the prototype's fake
   customer notes rendered in production as if real). */
const INITIAL_NOTES: PrivateNote[] = [];

function StatusPill({ status }: { status: FeedbackStatus }) {
  const open = status === 'open';
  return (
    <span className={'pf-status ' + (open ? 'pf-status--open' : 'pf-status--resolved')}>
      <span className="pf-status__dot" />
      {open ? 'Open' : 'Resolved'}
    </span>
  );
}

export function FeedbackGate() {
  // Real, wired config. Governs whether the private channel is on. We keep
  // the query mounted so the setting stays live; the inbox is shown when the
  // channel is enabled (or while the config is still loading / defaulted).
  const cfg = useFeedbackGate();
  const enabled = cfg.data?.config?.enabled ?? true;

  // Inbox list + resolve/reopen toggle -- local UI state until a real
  // /feedback-inbox endpoint exists. Starts EMPTY (never seeded). No
  // auto-anything; an explicit click flips one note open <-> resolved.
  const [notes, setNotes] = useState<PrivateNote[]>(INITIAL_NOTES);
  function toggleResolved(id: string) {
    setNotes((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, status: n.status === 'open' ? 'resolved' : 'open' } : n,
      ),
    );
  }

  const openCount = useMemo(() => notes.filter((n) => n.status === 'open').length, [notes]);
  const totalCount = notes.length;

  return (
    <div className="pf">
      {/* ---- POLICY BANNER (Hard Rule #4 stated plainly) ---- */}
      <div className="pf-policy">
        <ShieldIcon />
        <div className="pf-policy__text">
          <strong>Everyone gets the same ask.</strong> We never screen customers by
          predicted rating or route unhappy ones away from Google. This is simply an
          optional private channel offered alongside the public review link -- customers
          choose it themselves.
        </div>
      </div>

      {/* ---- CHANNEL-OFF STATE ----
          If the owner has turned the private channel off in settings, no new
          private notes can arrive. We say so honestly instead of implying a
          live inbox. (Config is the real wired value.) */}
      {!enabled && (
        <div className="pf-policy" style={{ background: 'var(--surface-2)', borderColor: 'var(--line-2)' }}>
          <div className="pf-policy__text">
            <strong>The private channel is currently off.</strong> Turn it on in Settings
            to offer customers a private way to reach you alongside the public review link.
            Existing notes stay here.
          </div>
        </div>
      )}

      {/* ---- STATS ---- */}
      <div className="pf-stats">
        <div className="pf-stat">
          <div className="pf-stat__lab">OPEN ITEMS</div>
          <div className="pf-stat__num pf-stat__num--open">{openCount}</div>
          <div className="pf-stat__sub">Waiting for you to follow up</div>
        </div>
        <div className="pf-stat">
          <div className="pf-stat__lab">TOTAL THIS MONTH</div>
          <div className="pf-stat__num">{totalCount}</div>
          <div className="pf-stat__sub">Private notes received</div>
        </div>
      </div>

      {/* ---- FEEDBACK LIST (honest empty state until notes really exist) ---- */}
      {notes.length === 0 && (
        <div className="pf-item" style={{ textAlign: 'center', padding: '28px 20px' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No private notes yet</div>
          <div style={{ fontSize: 13, color: 'var(--muted, #5f6368)', maxWidth: 480, margin: '0 auto' }}>
            When a customer chooses the private channel instead of a public review, their
            note lands here. The private-channel link is offered alongside the public
            review link in every review request.
          </div>
        </div>
      )}
      <div className="pf-list">
        {notes.map((n) => (
          <div className="pf-item" key={n.id}>
            <div className="pf-item__row">
              <div className="pf-item__ava" style={{ background: n.color }}>{n.initial}</div>
              <div className="pf-item__body">
                {/* Desktop header: name + topic + status + ago inline.
                    Mobile header (CSS): name over "topic - ago", status right. */}
                <div className="pf-item__head">
                  <span className="pf-item__names">
                    <span className="pf-item__name">{n.name}</span>
                    <span className="pf-item__meta">{n.topic} &middot; {n.ago}</span>
                  </span>
                  <span className="pf-item__topic">{n.topic}</span>
                  <StatusPill status={n.status} />
                  <span className="pf-item__ago">Private &middot; {n.ago}</span>
                </div>
                <p className="pf-item__text">{n.text}</p>
                <div className="pf-item__actions">
                  {/* "Reply privately" removed (launch QC): there is no contact
                      field on a private note and no send path, so it was a dead
                      button. Re-add it wired to a real compose action when a
                      /feedback-inbox endpoint provides the customer's contact. */}
                  <button
                    className="pf-btn pf-btn--ghost"
                    type="button"
                    onClick={() => toggleResolved(n.id)}
                  >
                    {n.status === 'open' ? (
                      <>
                        <span className="pf-btn__label pf-btn__label--full">Mark resolved</span>
                        <span className="pf-btn__label pf-btn__label--short">Resolve</span>
                      </>
                    ) : (
                      'Reopen'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
